if (!customElements.get('rr-spatial-directory')) {
  class RrSpatialDirectory extends HTMLElement {
    static dragThreshold = 8;

    connectedCallback() {
      if (this.isConnectedReady) return;
      this.isConnectedReady = true;
      this.abortController = new AbortController();
      this.startFrame = window.requestAnimationFrame(() => this.initialize());
    }

    disconnectedCallback() {
      this.isConnectedReady = false;
      this.initialized = false;
      this.abortController?.abort();
      window.cancelAnimationFrame(this.startFrame);
      window.cancelAnimationFrame(this.dragFrame);
      window.cancelAnimationFrame(this.reclampFrame);
      this.removeBreakpointListener();
      this.resizeObserver?.disconnect();
      this.backgroundVideo?.pause();
    }

    initialize() {
      if (!this.isConnected || this.initialized) return;
      this.initialized = true;
      this.stage = this.querySelector('[data-rrsd-stage]');
      this.directory = this.querySelector('[data-rrsd-directory]');
      this.objects = Array.from(this.querySelectorAll('[data-rrsd-object]'));
      this.draggingEnabled = this.dataset.draggingEnabled === 'true';
      this.motionEnabled = this.dataset.motionEnabled === 'true';
      this.backgroundVideo = this.querySelector('.rrsd__background-video');
      this.breakpoint = window.matchMedia('(min-width: 750px)');
      this.onBreakpointChange = () => this.updateBreakpoint();
      this.zOrder = [...this.objects];

      this.updateBreakpoint();
      this.addBreakpointListener();
      this.initializeInteractionOrder();
      this.bindDragging();
      this.observeBounds();
      this.prepareBackgroundVideo();
      this.bindThemeEditor();

      if (this.draggingEnabled) this.scheduleReclamp();
    }

    addBreakpointListener() {
      if (this.breakpoint.addEventListener) {
        this.breakpoint.addEventListener('change', this.onBreakpointChange);
      } else {
        this.breakpoint.addListener(this.onBreakpointChange);
      }
    }

    removeBreakpointListener() {
      if (!this.breakpoint || !this.onBreakpointChange) return;
      if (this.breakpoint.removeEventListener) {
        this.breakpoint.removeEventListener('change', this.onBreakpointChange);
      } else {
        this.breakpoint.removeListener(this.onBreakpointChange);
      }
    }

    updateBreakpoint() {
      const nextBreakpoint = this.breakpoint.matches ? 'desktop' : 'mobile';
      const breakpointChanged = this.dataset.breakpoint && this.dataset.breakpoint !== nextBreakpoint;
      this.dataset.breakpoint = nextBreakpoint;

      if (breakpointChanged) {
        this.resetVisitorPositions();
        this.dispatchEvent(
          new CustomEvent('rrsd:breakpoint-change', {
            bubbles: false,
            detail: { breakpoint: nextBreakpoint },
          })
        );
        this.scheduleReclamp();
      }
    }

    initializeInteractionOrder() {
      this.zOrder.forEach((object, index) => {
        object.style.setProperty('--rrsd-interaction-z', String(index + 1));
      });
    }

    bringToFront(object) {
      this.zOrder = this.zOrder.filter((item) => item !== object);
      this.zOrder.push(object);
      this.initializeInteractionOrder();
    }

    bindDragging() {
      if (!this.draggingEnabled || !this.directory) return;

      const signal = this.abortController.signal;
      this.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
      this.addEventListener('pointermove', (event) => this.onPointerMove(event), { passive: false, signal });
      this.addEventListener('pointerup', (event) => this.onPointerEnd(event), { signal });
      this.addEventListener('pointercancel', (event) => this.onPointerEnd(event), { signal });
      this.addEventListener('lostpointercapture', (event) => this.onPointerEnd(event), { signal });
      this.addEventListener('click', (event) => this.onClick(event), { capture: true, signal });
      this.addEventListener('dragstart', (event) => this.preventNativeDrag(event), { signal });
      this.addEventListener('selectstart', (event) => this.preventNativeDrag(event), { signal });
    }

    onPointerDown(event) {
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;

      const object = event.target.closest('[data-rrsd-object]');
      const link = event.target.closest('[data-rrsd-object-link]');
      if (!object || !link || !this.contains(object)) return;

      const objectRect = this.getRenderedObjectRect(object);
      const boundaryRect = this.directory.getBoundingClientRect();
      if (!objectRect || !boundaryRect.width || !boundaryRect.height) return;

      this.bringToFront(object);
      this.dragState = {
        pointerId: event.pointerId,
        pointerType: event.pointerType,
        object,
        link,
        captureTarget: object,
        startX: event.clientX,
        startY: event.clientY,
        startOffsetX: this.getObjectOffset(object, 'x'),
        startOffsetY: this.getObjectOffset(object, 'y'),
        minDeltaX: boundaryRect.left - objectRect.left,
        maxDeltaX: boundaryRect.right - objectRect.right,
        minDeltaY: boundaryRect.top - objectRect.top,
        maxDeltaY: boundaryRect.bottom - objectRect.bottom,
        dragging: false,
      };

      if (event.pointerType !== 'mouse') this.capturePointer(this.dragState.captureTarget, event.pointerId);
    }

    onPointerMove(event) {
      const state = this.dragState;
      if (!state || state.pointerId !== event.pointerId) return;

      const deltaX = event.clientX - state.startX;
      const deltaY = event.clientY - state.startY;

      if (!state.dragging && Math.hypot(deltaX, deltaY) < RrSpatialDirectory.dragThreshold) return;

      if (!state.dragging) {
        state.dragging = true;
        state.object.classList.add('is-dragging');
        if (state.pointerType === 'mouse') this.capturePointer(state.captureTarget, event.pointerId);
      }

      event.preventDefault();
      this.pendingDragPosition = {
        object: state.object,
        x: state.startOffsetX + this.clampDelta(deltaX, state.minDeltaX, state.maxDeltaX),
        y: state.startOffsetY + this.clampDelta(deltaY, state.minDeltaY, state.maxDeltaY),
      };
      this.scheduleDragFrame();
    }

    onPointerEnd(event) {
      const state = this.dragState;
      if (!state || state.pointerId !== event.pointerId) return;

      this.flushDragFrame();
      state.object.classList.remove('is-dragging');
      this.dragState = null;

      if (state.dragging) {
        this.suppressedLink = state.link;
        window.setTimeout(() => {
          if (this.suppressedLink === state.link) this.suppressedLink = null;
        }, 0);
      }

      this.releasePointerCapture(state.captureTarget, event.pointerId);
    }

    onClick(event) {
      const link = event.target.closest('[data-rrsd-object-link]');
      if (!link || link !== this.suppressedLink) return;
      event.preventDefault();
      event.stopPropagation();
      this.suppressedLink = null;
    }

    preventNativeDrag(event) {
      if (event.target.closest('[data-rrsd-object]')) event.preventDefault();
    }

    clampDelta(delta, minimum, maximum) {
      if (minimum <= maximum) return Math.min(Math.max(delta, minimum), maximum);
      return (minimum + maximum) / 2;
    }

    scheduleDragFrame() {
      if (this.dragFrame) return;
      this.dragFrame = window.requestAnimationFrame(() => {
        this.dragFrame = null;
        this.applyPendingDragPosition();
      });
    }

    flushDragFrame() {
      if (this.dragFrame) {
        window.cancelAnimationFrame(this.dragFrame);
        this.dragFrame = null;
      }
      this.applyPendingDragPosition();
    }

    applyPendingDragPosition() {
      if (!this.pendingDragPosition) return;
      const { object, x, y } = this.pendingDragPosition;
      this.setObjectOffset(object, x, y);
      this.pendingDragPosition = null;
    }

    getObjectOffset(object, axis) {
      return Number.parseFloat(object.dataset[`drag${axis.toUpperCase()}`]) || 0;
    }

    setObjectOffset(object, x, y) {
      const roundedX = Math.round(x * 100) / 100;
      const roundedY = Math.round(y * 100) / 100;
      object.dataset.dragX = String(roundedX);
      object.dataset.dragY = String(roundedY);
      object.style.setProperty('--rrsd-drag-x', `${roundedX}px`);
      object.style.setProperty('--rrsd-drag-y', `${roundedY}px`);
    }

    resetVisitorPositions() {
      const activeState = this.dragState;
      this.dragState = null;
      this.pendingDragPosition = null;

      if (activeState) this.releasePointerCapture(activeState.captureTarget, activeState.pointerId);

      this.objects.forEach((object) => {
        object.classList.remove('is-dragging');
        delete object.dataset.dragX;
        delete object.dataset.dragY;
        object.style.removeProperty('--rrsd-drag-x');
        object.style.removeProperty('--rrsd-drag-y');
      });
      this.zOrder = [...this.objects];
      this.initializeInteractionOrder();
    }

    getRenderedObjectRect(object) {
      const visibleParts = [object.querySelector('.rrsd__media'), object.querySelector('.rrsd__title')].filter(Boolean);
      const rects = visibleParts
        .map((part) => part.getBoundingClientRect())
        .filter((rect) => rect.width > 0 || rect.height > 0);

      if (!rects.length) {
        const fallback = object.getBoundingClientRect();
        return fallback.width > 0 || fallback.height > 0 ? this.expandRectForEffects(object, fallback) : null;
      }

      const firstRect = rects[0];
      const union = rects.slice(1).reduce(
        (union, rect) => ({
          left: Math.min(union.left, rect.left),
          right: Math.max(union.right, rect.right),
          top: Math.min(union.top, rect.top),
          bottom: Math.max(union.bottom, rect.bottom),
          width: Math.max(union.right, rect.right) - Math.min(union.left, rect.left),
          height: Math.max(union.bottom, rect.bottom) - Math.min(union.top, rect.top),
        }),
        {
          left: firstRect.left,
          right: firstRect.right,
          top: firstRect.top,
          bottom: firstRect.bottom,
          width: firstRect.width,
          height: firstRect.height,
        }
      );
      return this.expandRectForEffects(object, union);
    }

    expandRectForEffects(object, rect) {
      let expandedRect = rect;
      if (object.dataset.glowEnabled === 'true' && Number.parseFloat(object.dataset.glowStrength) > 0) {
        const glowRadius = Number.parseFloat(object.dataset.glowRadius) || 0;
        expandedRect = this.makeRect(
          rect.left - glowRadius,
          rect.top - glowRadius,
          rect.right + glowRadius,
          rect.bottom + glowRadius
        );
      }
      return this.expandRectForMotion(object, expandedRect);
    }

    expandRectForMotion(object, rect) {
      if (!this.motionEnabled) return rect;

      const motionType = object.dataset.motionType;
      if (!motionType || motionType === 'none') return rect;

      if (motionType === 'rotate') {
        const layerRect = object.querySelector('.rrsd__motion-layer')?.getBoundingClientRect();
        const centerX = layerRect ? (layerRect.left + layerRect.right) / 2 : (rect.left + rect.right) / 2;
        const centerY = layerRect ? (layerRect.top + layerRect.bottom) / 2 : (rect.top + rect.bottom) / 2;
        const radius = Math.max(
          Math.hypot(rect.left - centerX, rect.top - centerY),
          Math.hypot(rect.right - centerX, rect.top - centerY),
          Math.hypot(rect.left - centerX, rect.bottom - centerY),
          Math.hypot(rect.right - centerX, rect.bottom - centerY)
        );
        return this.makeRect(centerX - radius, centerY - radius, centerX + radius, centerY + radius);
      }

      const padding = motionType === 'drift' ? 36 : motionType === 'pulse' ? Math.max(rect.width, rect.height) * 0.04 : 18;
      return this.makeRect(rect.left - padding, rect.top - padding, rect.right + padding, rect.bottom + padding);
    }

    makeRect(left, top, right, bottom) {
      return { left, top, right, bottom, width: right - left, height: bottom - top };
    }

    capturePointer(element, pointerId) {
      try {
        element?.setPointerCapture?.(pointerId);
      } catch (_error) {
        // Touch-action still keeps direct-manipulation gestures on the object if capture is unavailable.
      }
    }

    releasePointerCapture(element, pointerId) {
      try {
        if (element?.hasPointerCapture?.(pointerId)) element.releasePointerCapture(pointerId);
      } catch (_error) {
        // The pointer may already be released by the browser.
      }
    }

    observeBounds() {
      if (!this.draggingEnabled || !this.directory) return;

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleReclamp());
        this.resizeObserver.observe(this.directory);
        this.objects.forEach((object) => this.resizeObserver.observe(object));
      } else {
        window.addEventListener('resize', () => this.scheduleReclamp(), {
          signal: this.abortController.signal,
        });
      }

      this.querySelectorAll('.rrsd__image').forEach((image) => {
        if (!image.complete) {
          image.addEventListener('load', () => this.scheduleReclamp(), {
            once: true,
            signal: this.abortController.signal,
          });
        }
      });
    }

    scheduleReclamp() {
      if (!this.draggingEnabled || this.reclampFrame) return;
      this.reclampFrame = window.requestAnimationFrame(() => {
        this.reclampFrame = null;
        this.reclampObjects();
      });
    }

    reclampObjects() {
      if (this.dragState?.dragging || !this.directory) return;
      const boundary = this.directory.getBoundingClientRect();
      if (!boundary.width || !boundary.height) return;

      const corrections = this.objects.map((object) => {
        const rect = this.getRenderedObjectRect(object);
        if (!rect) return null;

        let x = 0;
        let y = 0;

        if (rect.width > boundary.width) {
          x = (boundary.left + boundary.right - rect.left - rect.right) / 2;
        } else if (rect.left < boundary.left) {
          x = boundary.left - rect.left;
        } else if (rect.right > boundary.right) {
          x = boundary.right - rect.right;
        }

        if (rect.height > boundary.height) {
          y = (boundary.top + boundary.bottom - rect.top - rect.bottom) / 2;
        } else if (rect.top < boundary.top) {
          y = boundary.top - rect.top;
        } else if (rect.bottom > boundary.bottom) {
          y = boundary.bottom - rect.bottom;
        }

        return { object, x, y };
      });

      corrections.forEach((correction) => {
        if (!correction || (!correction.x && !correction.y)) return;
        this.setObjectOffset(
          correction.object,
          this.getObjectOffset(correction.object, 'x') + correction.x,
          this.getObjectOffset(correction.object, 'y') + correction.y
        );
      });
    }

    prepareBackgroundVideo() {
      if (!this.backgroundVideo) return;
      this.backgroundVideo.muted = true;
      this.backgroundVideo.defaultMuted = true;
      this.backgroundVideo.playsInline = true;
      this.backgroundVideo.loop = true;

      const revealVideo = () => {
        this.dataset.videoAutoplay = 'playing';
        if (this.dataset.backgroundType === 'intro_video') this.classList.add('is-video-ready');
      };
      this.backgroundVideo.addEventListener('playing', revealVideo, {
        once: true,
        signal: this.abortController.signal,
      });

      let playPromise;
      try {
        playPromise = this.backgroundVideo.play();
      } catch (_error) {
        this.dataset.videoAutoplay = 'blocked';
        return;
      }
      if (playPromise) {
        playPromise.then(revealVideo).catch(() => {
          this.dataset.videoAutoplay = 'blocked';
          this.classList.remove('is-video-ready');
        });
      }
    }

    bindThemeEditor() {
      this.addEventListener(
        'shopify:block:select',
        (event) => {
          const selected = event.target.closest('[data-rrsd-object]');
          if (!selected || !this.contains(selected)) return;
          this.objects.forEach((object) => object.classList.toggle('is-editor-selected', object === selected));
        },
        { signal: this.abortController.signal }
      );

      this.addEventListener(
        'shopify:block:deselect',
        () => this.objects.forEach((object) => object.classList.remove('is-editor-selected')),
        { signal: this.abortController.signal }
      );
    }
  }

  customElements.define('rr-spatial-directory', RrSpatialDirectory);
}
